import {
  describeFailure as describeApiFailure,
  type ApiFailure,
  type FailureCopy,
} from '../../api/client.ts'
import type { WebhookEndpointConfig, WebhookVariant } from '../../api/webhooks.ts'

// Formatting for the webhooks screen.
//
// Built on Intl rather than a date library: the portal ships React,
// @curri/components, Scalar and phosphor and nothing else, and a relative
// timestamp plus a percentage does not justify pulling date-fns into the bundle.
// (There is no shared relative-time helper in the repo to reuse — curri-app and
// curri-admin each have their own, and neither is importable from a package.)

const ABSOLUTE = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
]

export const formatRelative = (iso: string, now: number = Date.now()): string => {
  const elapsed = new Date(iso).getTime() - now

  for (const [unit, ms] of UNITS) {
    if (Math.abs(elapsed) >= ms) return RELATIVE.format(Math.round(elapsed / ms), unit)
  }
  return RELATIVE.format(Math.round(elapsed / 1000), 'second')
}

export const formatAbsolute = (iso: string): string =>
  ABSOLUTE.format(new Date(iso))

/**
 * Null is "nothing was sent", which is a different fact from 0%. Rendering it
 * through a percentage formatter would print "0%" and read as a total outage.
 */
export const formatSuccessRate = (rate: number | null): string =>
  rate === null ? '—' : `${(rate * 100).toFixed(rate >= 0.999 || rate === 0 ? 0 : 1)}%`

export const VARIANT_LABELS: Record<WebhookVariant, string> = {
  deliveries: 'Production',
  sandboxDeliveries: 'Sandbox',
  payloads: 'Production',
  sandboxPayloads: 'Sandbox',
}

/**
 * Only the legacy source is worth naming. `integrationSettings` is where an
 * endpoint normally lives, so labelling it told the reader nothing they could
 * act on; this one explains an endpoint that was set outside the portal.
 */
export const LEGACY_SOURCE_NOTE = 'Legacy account setting'

export const isLegacySource = (
  source: WebhookEndpointConfig['source']
): boolean => source === 'legacyColumn'

/** The shared failure copy, bound to what this screen shows. */
export const describeFailure = (failure: ApiFailure): FailureCopy =>
  describeApiFailure(failure, 'webhook history')
